import { mix } from 'polished'

import { themeHelpers } from '@subaio/utils'
import { defaultColors, defaultColorsDark, defaultInputTheme, defaultStatusTheme } from '@subaio/webapp'

const animation = {
	default: 'cubic-bezier(0.4, 0, 0.2, 1)',
}

const fontFamily = {
	header: `'Poppins', sans-serif`,
	base: `'Roboto', sans-serif`,
	code: `'JetBrains Mono', monospace`,
}

// TODO: Cleanup the colors in a separate track
const font = {
	family: fontFamily,
	size: {
		largest: '28px',
		larger: '24px',
		large: '20px',
		base: '15px',
		small: '13px',
		smaller: '12px',

		h1: `500 30px ${fontFamily.header}`,
		h2: `500 26px ${fontFamily.header}`,
		h3: `500 22px ${fontFamily.header}`,
		h4: `400 20px ${fontFamily.header}`,
		h5: `400 18px ${fontFamily.header}`,
	},
	weight: {
		/** 700 */
		bold: 700,
		/** 600, does not exist for Roboto or JetBrains */
		semiBold: 600,
		/** 500 */
		medium: 500,
		/** 400 */
		base: 400,
		/** 300 */
		thin: 300,
	},
}

const colorsLight = {
	primary: 'hsl(134, 70%, 18%)',
	primaryLightened: 'hsl(134, 39.70%, 85.70%)',
	primaryHover: 'hsl(134, 70%, 18%, 0.92)',
	primaryTranslucent: 'hsla(134, 34%, 73%, 0.2)',
	secondary: 'hsla(55, 93%, 73%, 1)',
	secondaryTranslucent: 'hsla(55, 93%, 73%, 0.4)',
	secondaryAccent: 'hsla(55, 95%, 82%, 1)',
	linkMuted: 'hsl(135, 48.00%, 60.00%)',
	pageHeaderBackground: 'hsl(158.71deg 24.41% 24.9%)',
	pageHeaderText: defaultColors.white,
	code: 'hsl(134, 40%, 18%)',
	codeTranslucent: 'hsla(134, 26%, 73%, 0.2)',
	backgroundInterval: 'hsla(134, 34%, 53%, 0.1)',

	/** Applies to all borders around elements, but it is not the same as a separator */
	border: 'hsla(220, 31%, 87%, 1)',

	textMuted: 'hsl(0, 0.00%, 47.10%)',
	textSelect: 'hsla(134, 52%, 62%, 0.8)',

	primaryAccent: '#115C22',
	/** Contrasts are the "background color" version */
	primaryContrast: '#a1d1ac',

	logo: defaultColors.black,
	logoHeader: defaultColors.white,

	/** Used to indicate a change that is generally user made and not yet saved */
	changed: '#FCA400',
	error: '#E15554',
	danger: '#E15554',
	/** Contrasts are the "background color" version */
	errorContrast: '#FFB3B3',
	focus: '#408550',
	/** Contrasts are the "background color" version */
	focusContrast: '#ACCAD9',
	/** Contrasts are the "background color" version */
	linkContrast: '#88a7c2',
	success: '#5EB870',
	/** Contrasts are the "background color" version */
	successContrast: '#9EDBAA',
	warning: '#F28F41',
	/** Contrasts are the "background color" version */
	warningContrast: '#FDE8D6',

	text: '#3c4257',
	/** Contrasts are the "background color" version */
	textContrast: '#FFF',
	textContrastMuted: 'hsl(0, 0%, 90%)',
	textHeader: '#202124',

	/** These indents are seen in the `<header />` and `<footer />` to separate them from `<main />` */
	pageIndent: '#DFDEDE',
	/** The page background color */
	background: '#FFF',
	/** Contrasts are the "background color" version */
	backgroundContrast: '#333333',
	/** This separator is intended to be used in cards or similarly colored backgrounds */
	separator: '#F2F2F2',
	/** The sidebar is using `color.primary` and can have separators in both the top and bottom */
	sidebarSeparator: '#1B4559',
	/** The scrollbar is custom in this application */
	scrollbarThumb: '#ADB1B2',

	disabled: '#808080',
	/** Contrasts are the "background color" version */
	disabledContrast: '#E0E0E0',
	/** Color of the label of all form field labels (Name over input fields) */
	inputLabel: '#202124',
	/** Color of the small (usually interactable) indicators such as chevrons or close icons */
	indicator: '#696969',
	/** Color of the small interactable indicators such as chevrons or close icons on hover */
	indicatorHover: '#333333',

	// -------------------------------

	iconGradientStart: defaultColors.gradientBackgroundStart,
	iconGradientStop: defaultColors.gradientBackgroundStop,

	cardBackground: defaultColors.white,
	cardBackgroundGradient: defaultColors.lightestGray,
	cardBackgroundGradientHover: defaultColors.brandGreenOpaque,

	iconHighlightBackground: defaultColors.brandGreenTranslucent,
	iconHighlightColor: defaultColors.brandGreenLight,

	tableHeader: defaultColors.lightestGray,

	link: defaultColors.brandGreenLight,
	linkBackgroundHover: defaultColors.lighterGray,
	linkBackgroundActive: defaultColors.brandGreenOpaque,

	codeBlockBackground: defaultColors.lightestGray,
	codeBlockBackgroundHeader: defaultColors.white,

	button: {
		focusOutline: '#408550',
	},

	input: {
		...defaultInputTheme,
		text: 'hsl(0, 0%, 0%)',
		textFaded: 'hsl(0, 0%, 20%)',
		textHint: 'hsl(0, 0%, 40%)',
		textPlaceholder: 'hsl(0, 0%, 60%)',
		textContrast: 'hsl(0, 0%, 100%)',
		background: 'hsl(0, 0%, 98%)',
		backgroundHover: 'hsl(0, 0%, 100%)',
		error: 'hsl(0, 80%, 51%)',
		errorContrast: 'hsl(0, 80%, 80%)',
		errorContrastHover: 'hsl(0, 80%, 85%)',
		focus: 'hsl(134, 70%, 18%)',
		border: 'hsl(0, 0%, 80%)',
		borderHover: 'hsl(0, 0%, 70%)',
		disabled: '#808080',
		disabledContrast: '#E0E0E0',
		chipBackground: 'hsl(0, 0%, 85%)',

		code: {
			text: defaultColors.black,
			background: defaultColors.lightestGray,
			border: defaultColors.lightGray,
			disabled: defaultColors.gray,
			focus: defaultColors.brandGreenAccent,
			disabledContrast: defaultColors.lighterGray,
		},
	},

	/** System status indicators */
	status: defaultStatusTheme,
}

const colorsDark: typeof colorsLight = {
	secondary: 'hsla(55, 93%, 73%, 1)',
	secondaryTranslucent: 'hsla(55, 93%, 73%, 0.4)',
	secondaryAccent: 'hsla(55, 95%, 82%, 1)',
	pageHeaderBackground: 'hsl(158, 24.41%, 24.9%)',
	pageHeaderText: defaultColors.white,
	backgroundInterval: 'hsla(134, 34%, 53%, 0.1)',

	primaryAccent: 'hsl(155, 56.30%, 43.90%)',
	/** Contrasts are the "background color" version */
	primaryContrast: '#a1d1ac',

	logo: defaultColors.white,
	logoHeader: defaultColors.white,

	/** Used to indicate a change that is generally user made and not yet saved */
	changed: '#FCA400',
	error: defaultColorsDark.accentRed,
	danger: defaultColorsDark.accentRed,
	/** Contrasts are the "background color" version */
	errorContrast: '#FFB3B3',
	focus: '#408550',
	/** Contrasts are the "background color" version */
	focusContrast: '#ACCAD9',
	/** Contrasts are the "background color" version */
	linkContrast: '#88a7c2',
	success: defaultColorsDark.accentGreen,
	/** Contrasts are the "background color" version */
	successContrast: mix(0.15, defaultColorsDark.accentGreen, defaultColorsDark.darkestGray),
	warning: defaultColorsDark.accentOrange,
	/** Contrasts are the "background color" version */
	warningContrast: mix(0.15, defaultColorsDark.accentOrange, defaultColorsDark.darkestGray),

	/** Contrasts are the "background color" version */
	textContrast: 'hsl(0, 0%, 11%)',
	textContrastMuted: 'hsl(0, 0%, 90%)',

	/** The page background color */
	/** Contrasts are the "background color" version */
	backgroundContrast: '#333333',
	/** The sidebar is using `color.primary` and can have separators in both the top and bottom */
	sidebarSeparator: '#1B4559',
	/** The scrollbar is custom in this application */
	scrollbarThumb: '#ADB1B2',

	disabled: '#808080',
	/** Contrasts are the "background color" version */
	disabledContrast: '#E0E0E0',
	/** Color of the label of all form field labels (Name over input fields) */
	inputLabel: '#202124',
	/** Color of the small (usually interactable) indicators such as chevrons or close icons */
	indicator: '#696969',
	/** Color of the small interactable indicators such as chevrons or close icons on hover */
	indicatorHover: '#333333',

	iconGradientStart: '#bed4c3',
	iconGradientStop: '#9eb5a3',

	// -------------------------------
	primary: defaultColorsDark.brandGreen,
	primaryHover: defaultColorsDark.brandGreenAccent,
	primaryTranslucent: defaultColorsDark.brandGreenTranslucent,
	primaryLightened: defaultColorsDark.brandGreenAccent,

	text: defaultColorsDark.lightestGray,
	textHeader: defaultColorsDark.white,
	textMuted: defaultColorsDark.lighterGray,
	textSelect: defaultColorsDark.brandGreenTranslucent,
	// background: defaultColorsDark.darkestGray,
	background: defaultColorsDark.darkestGray,

	link: defaultColorsDark.brandGreenLight,
	linkMuted: defaultColorsDark.brandGreenAccent,
	linkBackgroundHover: defaultColorsDark.brandGreenTranslucent,
	linkBackgroundActive: defaultColorsDark.brandGreenOpaque,

	code: defaultColorsDark.brandGreenLight,
	codeTranslucent: defaultColorsDark.gray,

	border: defaultColorsDark.lightGreenGray,
	pageIndent: defaultColorsDark.lightGreenGray,
	separator: defaultColorsDark.lightGreenGray,

	cardBackground: defaultColorsDark.darkestGray,
	cardBackgroundGradient: defaultColorsDark.darkestGray,
	cardBackgroundGradientHover: defaultColorsDark.brandGreenTranslucent,

	iconHighlightBackground: defaultColorsDark.brandGreenAccent,
	iconHighlightColor: defaultColorsDark.brandGreenLight,

	tableHeader: defaultColorsDark.gray,

	codeBlockBackground: defaultColorsDark.darkGray,
	codeBlockBackgroundHeader: 'hsl(187.7deg, 49.4%, 18.6%)',

	button: {
		focusOutline: '#408550',
	},

	input: {
		...defaultInputTheme,
		text: defaultColorsDark.white,
		textFaded: defaultColorsDark.lighterGray,
		textHint: defaultColorsDark.lightGray,
		textPlaceholder: defaultColorsDark.lighterGray,
		textContrast: defaultColorsDark.black,
		background: defaultColorsDark.darkestGray,
		backgroundHover: 'hsl(0, 0%, 100%)',
		error: 'hsl(0, 80%, 51%)',
		errorContrast: 'hsl(0, 80%, 80%)',
		errorContrastHover: 'hsl(0, 80%, 85%)',
		focus: 'hsl(134, 70%, 18%)',
		border: 'hsl(0, 0%, 80%)',
		borderHover: 'hsl(0, 0%, 70%)',
		disabled: '#808080',
		disabledContrast: '#E0E0E0',
		chipBackground: 'hsl(0, 0%, 85%)',

		code: {
			text: 'hsl(0, 0.00%, 100.00%)',
			background: 'hsl(187, 78.30%, 9.00%)',
			border: 'hsl(188, 49.50%, 18.60%)',
			disabled: 'hsl(164, 18.00%, 52.20%)',
			focus: 'hsl(55, 93%, 73%)',
			disabledContrast: 'hsl(189, 53.10%, 12.50%)',
		},
	},

	/** System status indicators */
	status: defaultStatusTheme,
}

export const colors = {
	light: colorsLight,
	dark: colorsDark,
}

type ThemeOptions = {
	mode: 'desktop' | 'mobile'
	colorScheme: 'light' | 'dark'
}

export const getTheme = ({ mode, colorScheme }: ThemeOptions) => ({
	color: colors[colorScheme],
	animation,
	font,
	mode,
	colorScheme,
})

export const helpers = themeHelpers(
	getTheme({
		mode: 'desktop',
		colorScheme: 'light',
	}),
)

export type Theme = ReturnType<typeof getTheme>
